import {
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  RecaptchaVerifier,
  type MultiFactorAssertion,
  type MultiFactorResolver,
  type Auth,
} from 'firebase/auth';

/** Thrown when email/password sign-in needs a second factor before completing. */
export class MfaRequiredError extends Error {
  readonly code = 'auth/multi-factor-auth-required';

  constructor() {
    super('Multi-factor authentication required.');
    this.name = 'MfaRequiredError';
  }
}

/** True for Firebase Auth `MultiFactorError` (not extension SW wrapper errors). */
export function isMultiFactorAuthRequired(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const e = error as { code?: string; customData?: { operationType?: string } };
  return (
    e.code === 'auth/multi-factor-auth-required' &&
    typeof e.customData?.operationType === 'string'
  );
}

export type MfaHintSummary = {
  index: number;
  factorId: string;
  displayName?: string | null;
  phoneNumber?: string | null;
};

export type MfaPendingState = {
  email: string;
  hints: MfaHintSummary[];
};

export function summarizeMfaHints(resolver: MultiFactorResolver): MfaHintSummary[] {
  return resolver.hints.map((hint, index) => ({
    index,
    factorId: hint.factorId,
    displayName: 'displayName' in hint ? (hint.displayName as string | null) : null,
    phoneNumber: 'phoneNumber' in hint ? (hint.phoneNumber as string | null) : null,
  }));
}

export function beginMfaResolver(auth: Auth, error: unknown): MultiFactorResolver {
  if (!isMultiFactorAuthRequired(error)) {
    throw new Error('Not a Firebase multi-factor auth error.');
  }
  return getMultiFactorResolver(auth, error as Parameters<typeof getMultiFactorResolver>[1]);
}

export function isTotpFactor(factorId: string): boolean {
  return factorId === TotpMultiFactorGenerator.FACTOR_ID;
}

export function isPhoneFactor(factorId: string): boolean {
  return factorId === PhoneMultiFactorGenerator.FACTOR_ID;
}

export function buildMfaAssertion(
  resolver: MultiFactorResolver,
  hintIndex: number,
  code: string,
  phoneVerificationId?: string
): MultiFactorAssertion {
  const hint = resolver.hints[hintIndex];
  if (!hint) {
    throw new Error('Invalid MFA factor.');
  }
  const trimmed = code.replace(/\s/g, '');
  if (isTotpFactor(hint.factorId)) {
    return TotpMultiFactorGenerator.assertionForSignIn(hint.uid, trimmed);
  }
  if (isPhoneFactor(hint.factorId)) {
    if (!phoneVerificationId) {
      throw new Error('PHONE_VERIFICATION_ID_REQUIRED');
    }
    const cred = PhoneAuthProvider.credential(phoneVerificationId, trimmed);
    return PhoneMultiFactorGenerator.assertion(cred);
  }
  throw new Error(`Unsupported MFA factor: ${hint.factorId}`);
}

export async function sendPhoneMfaVerification(
  auth: Auth,
  resolver: MultiFactorResolver,
  hintIndex: number,
  recaptchaVerifier: RecaptchaVerifier
): Promise<string> {
  const hint = resolver.hints[hintIndex];
  if (!hint || !isPhoneFactor(hint.factorId)) {
    throw new Error('Selected factor is not phone SMS.');
  }
  const phoneAuthProvider = new PhoneAuthProvider(auth);
  return phoneAuthProvider.verifyPhoneNumber(
    { multiFactorHint: hint, session: resolver.session },
    recaptchaVerifier
  );
}

export function createInvisibleRecaptcha(auth: Auth, containerId: string): RecaptchaVerifier {
  return new RecaptchaVerifier(auth, containerId, { size: 'invisible' });
}
